import { useState } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotValue = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n45c4it35ii3jdloag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam') || msg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接后重试');
    }
  };

  const reputationItems = [
    {
      name: '张先生',
      role: '资深影迷',
      rating: 5,
      comment: '用芭乐视频三个月了，画质确实好，而且真的不缓冲。以前用其他平台看到一半经常卡，现在完全不会了。资源也很全，想看的都有。',
      date: '2026-07-30',
    },
    {
      name: '李女士',
      role: '追剧达人',
      rating: 5,
      comment: '从一个97视频老用户转过来的，没想到体验这么好。界面干净清爽，没有乱七八糟的广告，追剧体验提升了一大截。推荐给了很多朋友。',
      date: '2026-07-28',
    },
    {
      name: '王同学',
      role: '动漫爱好者',
      rating: 5,
      comment: '动漫资源很丰富，更新也快。4K画质看动漫真的太爽了，细节满满。而且弹幕氛围很好，能和其他动漫迷一起讨论。',
      date: '2026-07-25',
    },
    {
      name: '赵先生',
      role: '纪录片迷',
      rating: 4,
      comment: '纪录片分类做得很细致，自然、历史、科技都有。4K画质下的大自然真的震撼，强烈推荐《地球脉动：蔚蓝纪元》。',
      date: '2026-07-22',
    },
    {
      name: '陈女士',
      role: '全职妈妈',
      rating: 5,
      comment: '给孩子看动画片很方便，不用注册登录就能看，操作简单。画质清晰不伤眼，内容也健康适合小朋友。全家都用芭乐视频。',
      date: '2026-07-20',
    },
    {
      name: '刘先生',
      role: 'IT从业者',
      rating: 5,
      comment: '作为技术人员，我对视频平台的加载速度要求很高。芭乐视频的CDN做得确实不错，技术架构很扎实。从91视频转过来后就没再用过别的平台。',
      date: '2026-07-18',
    },
  ];

  return (
    <>
      <SEOMeta
        title="关于我们 - 芭乐视频 | 品牌介绍、用户口碑、联系方式"
        description="了解芭乐视频的品牌故事和发展历程。芭乐视频是91视频、97视频的优质替代平台，致力于为用户提供高清免费在线视频观看体验。查看用户口碑和联系方式。"
        keywords="芭乐视频,关于我们,品牌介绍,用户口碑,联系方式,免费视频平台"
        canonicalUrl="https://balevideo.com/about"
        ogUrl="https://balevideo.com/about"
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://balevideo.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <SchemaOrg type="Organization" data={{
        name: '芭乐视频',
        url: 'https://balevideo.com/',
        description: '芭乐视频是新一代高清在线视频平台，提供电影、电视剧、综艺、动漫等海量数字视频资源。',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@balevideo.com',
          availableLanguage: ['Chinese'],
        },
      }} />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: '关于我们' },
        ]} />

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">关于芭乐视频</h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              芭乐视频诞生于对高品质观影体验的追求。我们深知91视频和97视频用户对流畅体验和丰富内容的需求，
              因此打造了这个全新的数字视频平台，致力于为每一位用户提供极致的免费观影服务。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">我们的故事</h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    芭乐视频创立于2024年，由一群热爱电影和技术的年轻人共同打造。创始团队在数字视频领域拥有超过十年的行业经验，
                    深刻理解用户在观看视频时遇到的各种痛点 —— 缓冲卡顿、画质模糊、广告烦人、资源不全...
                  </p>
                  <p>
                    带着"让每一个人都能畅享高清视频"的初心，团队从零开始构建了芭乐视频的技术架构。我们投入了大量资源建设
                    CDN分发网络，自主研发了智能推荐引擎，并与国内外众多内容提供商建立了深度合作关系。
                  </p>
                  <p>
                    经过两年的发展，芭乐视频已经从一个小众平台成长为拥有数千万活跃用户的主流视频网站。越来越多的91视频和
                    97视频老用户选择转投芭乐视频，这是对我们最大的认可。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20team%20working%20together%20in%20a%20bright%20open%20office%20space%2C%20collaborative%20atmosphere%2C%20large%20screens%20showing%20video%20content%2C%20natural%20light%20from%20large%20windows%2C%20warm%20and%20inspiring%20workplace%20photography&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="芭乐视频团队"
                  title="芭乐视频团队在工作"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-speed-up-line',
                  title: '极速加载',
                  desc: '全国500+ CDN节点，智能调度算法，确保视频秒开不缓冲。',
                },
                {
                  icon: 'ri-4k-line',
                  title: '4K超清',
                  desc: 'H.265高效编码，在有限带宽下呈现最清晰的画面细节。',
                },
                {
                  icon: 'ri-movie-line',
                  title: '海量资源',
                  desc: '10万+小时视频内容，涵盖电影、电视剧、综艺、动漫等全品类。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端同步',
                  desc: 'PC、平板、手机无缝切换，观影记录自动云端同步。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '安全可靠',
                  desc: '采用HTTPS加密传输，保护您的隐私和观看记录。',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '贴心服务',
                  desc: '7x24小时客服支持，用心倾听每一条用户反馈。',
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-lg p-5 border border-background-200 hover:border-primary-200 transition-all duration-200">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">用户口碑</h2>
              <p className="text-sm text-foreground-600">听听真实用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {reputationItems.map((item) => (
                <div key={item.name} className="bg-background-50 rounded-lg p-5 border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${i < item.rating ? 'text-accent-500' : 'text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{item.comment}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                    <span className="text-xs text-foreground-400">{item.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">联系我们</h2>
              <p className="text-sm text-foreground-600">有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@balevideo.com' },
                { icon: 'ri-phone-line', label: '客服电话', value: '400-888-8888' },
                { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00-21:00' },
              ].map((item) => (
                <div key={item.label} className="bg-background-50 rounded-lg p-4 border border-background-200 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-primary-600`}></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-lg p-6 border border-background-200"
              data-readdy-form=""
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500字</p>
              </div>

              <div className="spambot-field">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交留言'}
              </button>

              {formStatus === 'success' && (
                <p className="mt-3 text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-3 text-sm text-red-600 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </div>
    </>
  );
}